// Admin: control panel, the 13 simulated devices and their wire log, dashcam
// clips, and the sandbox browsers currently running.
import { useState } from 'react';
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { Weather } from '../../convex/shared/validators';
import type { VehicleView } from '../../convex/shared/views';
import { ControlPanel } from '../components/ControlPanel';
import { WEATHER_ICON } from '../components/Hud';
import { LiveScreen } from '../components/LiveScreen';
import { PROVIDER_LABEL, PROVIDER_TONE } from '../components/VehiclePanel';
import { fmtClockSeconds, fmtDuration, fmtRelative, humanize, shortName } from '../lib/format';
import { useCast, useNow } from '../lib/hooks';
import { useSimMutation, useSimQuery } from '../lib/session';
import { Button, Chip, Empty, Panel, Segmented, Spinner } from '../ui/primitives';
import { useToasts } from '../ui/Toasts';

export function AdminView() {
  const now = useNow(10_000);
  return (
    <div className="page">
      <header className="page-head">
        <h1>Admin</h1>
        <p>Switches, spend, devices and the wire log. Telemetry goes to the aa-app dev edge; mode (off, dry run, live) is the deployment env TELEMETRY_MODE.</p>
      </header>
      <div className="two-col">
        <ControlPanel />
        <div className="stack">
          <WeatherPanel now={now} />
          <Sessions now={now} />
        </div>
      </div>
      <Devices now={now} />
      <Clips now={now} />
    </div>
  );
}

const WEATHERS: Weather[] = ['clear', 'cloudy', 'rain', 'storm', 'fog', 'heat'];
const OVERRIDE_MINUTES = [30, 60, 180] as const;

/** Owner weather override (world.environment.overrideWeather): rain and storms slow traffic and raise hazard rates. */
function WeatherPanel({ now }: { now: number }) {
  const world = useSimQuery(api.world.environment.getWorldState, {});
  const override = useSimMutation(api.world.environment.overrideWeather);
  const toasts = useToasts();
  const [weather, setWeather] = useState<Weather>('rain');
  const [minutes, setMinutes] = useState<string>('60');
  const [busy, setBusy] = useState(false);
  return (
    <Panel title="Weather" icon={world ? WEATHER_ICON[world.weather] : 'cloud'}>
      <p className="muted">
        {world
          ? `Now ${humanize(world.weather).toLowerCase()}, ${Math.round(world.temperatureC)}°C${world.weatherUntil > now ? `, until about ${fmtClockSeconds(world.weatherUntil).slice(0, 5)}` : ''}.`
          : 'The world has not ticked yet.'}{' '}
        Rain, fog and storms slow traffic and make harsh events and collisions more likely.
      </p>
      <div className="weather-pick">
        <Segmented<Weather> ariaLabel="Weather" value={weather} onChange={setWeather} options={WEATHERS.map((w) => ({ value: w, label: humanize(w), icon: WEATHER_ICON[w] }))} />
        <Segmented<string> ariaLabel="For how long" value={minutes} onChange={setMinutes} options={OVERRIDE_MINUTES.map((m) => ({ value: String(m), label: m < 60 ? `${m} min` : `${m / 60} h` }))} />
        <Button
          tone="sky"
          icon={WEATHER_ICON[weather]}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await override({ weather, forMinutes: Number(minutes) });
              toasts.push(`${humanize(weather)} for the next ${Number(minutes) < 60 ? `${minutes} minutes` : `${Number(minutes) / 60} h`}.`, { tone: 'leaf', icon: WEATHER_ICON[weather] });
            } catch (err) {
              toasts.push(err instanceof Error ? err.message : 'Could not change the weather.', { tone: 'danger', icon: 'alert' });
            } finally {
              setBusy(false);
            }
          }}
        >
          Set the weather
        </Button>
      </div>
    </Panel>
  );
}

function Sessions({ now }: { now: number }) {
  const cast = useCast();
  const sessions = useSimQuery(api.browser.sessions.listLiveSessions, {});
  return (
    <Panel title="Sandbox browsers" icon="desktop">
      {sessions === undefined ? <Spinner /> : null}
      {sessions?.length === 0 ? <Empty icon="desktop" title="No browsers running">Personas open one when they decide to use aa-app.</Empty> : null}
      <ul className="session-list">
        {sessions?.map((s) => {
          const p = s.personaId ? cast?.byId.get(s.personaId) : undefined;
          return (
            <li key={s._id}>
              <LiveScreen session={s} />
              <div>
                <strong>{p ? <Link href={`/agent/${p.key}`}>{shortName(p.name)}</Link> : s.purpose === 'render' ? 'Dashcam render' : 'Browser'}</strong>
                <span className="muted">
                  {humanize(s.status)} · {s.breakpoint} · {fmtDuration(now - s.startedAt)}
                </span>
                {s.currentTaskGoal ? <p>{s.currentTaskGoal}</p> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function Devices({ now }: { now: number }) {
  const devices = useSimQuery(api.telemetry.devices.listDevices, {});
  const vehicles: VehicleView[] | undefined = useSimQuery(api.world.vehicles.listVehicles, {});
  const [deviceId, setDeviceId] = useState<Id<'devices'> | undefined>(undefined);
  const emissions = useSimQuery(api.telemetry.emitter.listRecentEmissions, { deviceId, limit: 60 });
  const vehicleName = (id: string) => vehicles?.find((v) => v._id === id)?.name ?? '—';
  const latest = emissions?.[0];
  const mode = latest ? (latest.status === -1 ? 'Dry run (built, not sent)' : 'Live (posting to the dev edge)') : 'No emissions yet';

  return (
    <Panel title="Devices and wire log" icon="send" actions={<Chip tone={latest?.status === -1 ? 'sun' : latest ? 'leaf' : 'neutral'}>{mode}</Chip>}>
      {devices === undefined ? <Spinner /> : null}
      {devices?.length === 0 ? <Empty icon="send" title="No devices yet">Seed the scenario; then provision them on the dev control plane (DESIGN.md §7).</Empty> : null}
      {devices && devices.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Provider</th>
                <th>Device id</th>
                <th>Model</th>
                <th>Last post</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d._id} className={deviceId === d._id ? 'is-selected' : ''} onClick={() => setDeviceId(deviceId === d._id ? undefined : d._id)}>
                  <td>{vehicleName(d.vehicleId)}</td>
                  <td>
                    <Chip tone={PROVIDER_TONE[d.provider]}>{PROVIDER_LABEL[d.provider]}</Chip>
                  </td>
                  <td>
                    <code>{d.providerId}</code>
                  </td>
                  <td>{d.model}</td>
                  <td>{d.lastEmitAt ? fmtRelative(d.lastEmitAt, now) : '—'}</td>
                  <td>{!d.enabled ? <Chip tone="neutral">Disabled</Chip> : d.lastStatus === undefined ? '—' : <StatusChip status={d.lastStatus} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <h3 className="sub-head">{deviceId ? 'Wire log for the selected device' : 'Wire log (all devices)'}</h3>
      {emissions === undefined ? <Spinner /> : null}
      {emissions?.length === 0 ? <p className="muted">Nothing on the wire yet.</p> : null}
      {emissions && emissions.length > 0 ? (
        <div className="table-wrap">
          <table className="table is-dense">
            <thead>
              <tr>
                <th>Time</th>
                <th>Provider</th>
                <th>Hook</th>
                <th>Samples</th>
                <th>Bytes</th>
                <th>Latency</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {emissions.map((e) => (
                <tr key={e._id}>
                  <td>{fmtClockSeconds(e.at)}</td>
                  <td>{e.provider}</td>
                  <td>
                    <code>{e.hook}</code>
                  </td>
                  <td>{e.sampleCount}</td>
                  <td>{e.bytes}</td>
                  <td>{e.latencyMs} ms</td>
                  <td title={e.error}>
                    <StatusChip status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Panel>
  );
}

function StatusChip({ status }: { status: number }) {
  if (status === -1) return <Chip tone="sun">Dry run</Chip>;
  if (status === 0) return <Chip tone="danger">Network error</Chip>;
  return <Chip tone={status >= 200 && status < 300 ? 'leaf' : 'danger'}>{status}</Chip>;
}

function Clips({ now }: { now: number }) {
  const clips = useSimQuery(api.telemetry.clips.listClips, { limit: 12 });
  return (
    <Panel title="Dashcam clips" icon="video">
      {clips === undefined ? <Spinner /> : null}
      {clips?.length === 0 ? <Empty icon="video" title="No clips yet">VisionMax incidents worth a video are rendered from the cab and sent to aa-app.</Empty> : null}
      <div className="clips">
        {clips?.map((c) => (
          <figure key={c._id} className="clip">
            {c.videoUrl ? <video src={c.videoUrl} controls preload="metadata" playsInline /> : <div className="clip-pending">{humanize(c.status)}</div>}
            <figcaption>
              <code>{c.ticket}</code>
              <span>
                {humanize(c.status)} · {fmtRelative(c.createdAt, now)} · {fmtDuration(c.endAt - c.startAt)}
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Panel>
  );
}
